import { Fragment, useId, useMemo } from 'react';
import { Search, Checkbox } from '@carbon/react';
import { ChevronRight, ChevronDown } from '@carbon/react/icons';
import { format0 } from 'utils/number';
import { EMPTY_CELL } from 'utils/empty-cell';
import style from './state-county-tree.module.scss';

export interface StateCountyBucket {
  id: string;
  name: string;
  count?: number;
  /** A county bucket's state id. */
  stateId?: string;
}

/**
 * A county's name under its state row, without the redundant ", AB" suffix the
 * facet appends.
 * @param {string} name The county bucket's name.
 * @return {string} The name without its state suffix.
 */
export function countyLabel(name: string): string {
  return name.replace(/,\s*[A-Za-z]{2}$/, '');
}

/**
 * Expandable State → County tree over document facet buckets. A checked
 * county's state is always selected, and unchecking a state clears its
 * counties. One search box narrows both levels and auto-expands states with a
 * matching county. Search text and the expanded set are owned by the caller so
 * they survive the tree being unmounted (e.g. map-query drops it when facets
 * are empty).
 * @param {object} props The buckets, selections, search and expanded rows, with
 *   their change handlers.
 * @return {JSX.Element} The tree.
 */
export function StateCountyTree({
  states,
  counties,
  selectedStates,
  selectedCounties,
  onStatesChange,
  onCountiesChange,
  search,
  onSearchChange,
  expandedIds,
  onExpandedIdsChange,
}: {
  states: StateCountyBucket[];
  counties: StateCountyBucket[];
  selectedStates: string[];
  selectedCounties: string[];
  onStatesChange: (ids: string[]) => void;
  onCountiesChange: (ids: string[]) => void;
  search: string;
  onSearchChange: (value: string) => void;
  expandedIds: Record<string, boolean>;
  onExpandedIdsChange: (next: Record<string, boolean>) => void;
}) {
  // Scope DOM ids to this instance: two trees can render at once now that this
  // is a shared component.
  const uid = useId();

  const countiesByState = useMemo(() => {
    const map = new Map<string, StateCountyBucket[]>();
    for (const county of counties) {
      const key = county.stateId ?? '';
      const list = map.get(key);
      if (list) list.push(county);
      else map.set(key, [county]);
    }
    return map;
  }, [counties]);

  const stateIdByCounty = useMemo(() => {
    const map = new Map<string, string>();
    for (const county of counties) {
      if (county.stateId) map.set(county.id, county.stateId);
    }
    return map;
  }, [counties]);

  /**
   * Selects or clears a state; clearing it clears its counties too.
   * @param {string} id The state's id.
   * @param {boolean} checked Whether it is now selected.
   */
  const toggleState = (id: string, checked: boolean) => {
    onStatesChange(
      checked ? [...selectedStates, id] : selectedStates.filter((v) => v !== id)
    );
    if (!checked) {
      const countyIds = new Set(
        (countiesByState.get(id) ?? []).map((c) => c.id)
      );
      if (countyIds.size) {
        onCountiesChange(selectedCounties.filter((v) => !countyIds.has(v)));
      }
    }
  };
  /**
   * Selects or clears a county; selecting it selects its state too.
   * @param {string} id The county's id.
   * @param {boolean} checked Whether it is now selected.
   */
  const toggleCounty = (id: string, checked: boolean) => {
    onCountiesChange(
      checked
        ? [...selectedCounties, id]
        : selectedCounties.filter((v) => v !== id)
    );
    if (checked) {
      const stateId = stateIdByCounty.get(id);
      if (stateId && !selectedStates.includes(stateId)) {
        onStatesChange([...selectedStates, stateId]);
      }
    }
  };

  const q = search.trim().toLowerCase();
  const rows = states
    .map((state) => {
      const stateCounties = countiesByState.get(state.id) ?? [];
      const stateMatches = !q || state.name.toLowerCase().includes(q);
      const matched = q
        ? stateCounties.filter((c) =>
            countyLabel(c.name).toLowerCase().includes(q)
          )
        : stateCounties;
      if (q && !stateMatches && matched.length === 0) return null;
      // A state matched by its own name (not just by a matching county) still
      // shows all its counties, so they stay visible and selectable.
      const shown = q
        ? stateMatches
          ? stateCounties
          : matched
        : stateCounties;
      return { state, counties: stateCounties, shown };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);

  const anyExpanded = Object.values(expandedIds).some(Boolean);
  /**
   * Collapses every state when any is open, else expands them all.
   * @return {void}
   */
  const toggleAll = () =>
    onExpandedIdsChange(
      anyExpanded ? {} : Object.fromEntries(states.map((s) => [s.id, true]))
    );
  /**
   * Expands or collapses one state's counties.
   * @param {string} id The state's id.
   * @return {void}
   */
  const toggleExpand = (id: string) =>
    onExpandedIdsChange({ ...expandedIds, [id]: !expandedIds[id] });

  return (
    <div className={style.tree}>
      <Search
        id={`${uid}-search`}
        labelText="Search states and counties"
        placeholder="Search states and counties..."
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        onClear={() => onSearchChange('')}
        size="md"
      />
      <table className={style.treeTable}>
        <thead>
          <tr>
            <th className={style.treeExpandCol}>
              <button
                type="button"
                className={style.treeExpandBtn}
                aria-label={anyExpanded ? 'Collapse all' : 'Expand all'}
                onClick={toggleAll}
              >
                {anyExpanded ? <ChevronDown /> : <ChevronRight />}
              </button>
            </th>
            <th>State / County</th>
            <th className={style.treeNum}>Counties</th>
            <th className={style.treeNum}>Documents</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ state, counties: stateCounties, shown }) => {
            const expanded = q
              ? shown.length > 0
              : (expandedIds[state.id] ?? false);
            return (
              <Fragment key={state.id}>
                <tr>
                  <td className={style.treeExpandCol}>
                    {stateCounties.length > 0 && (
                      <button
                        type="button"
                        className={style.treeExpandBtn}
                        aria-label={expanded ? 'Collapse' : 'Expand'}
                        aria-expanded={expanded}
                        onClick={() => toggleExpand(state.id)}
                      >
                        {expanded ? <ChevronDown /> : <ChevronRight />}
                      </button>
                    )}
                  </td>
                  <td>
                    <Checkbox
                      id={`${uid}-state-${state.id}`}
                      labelText={state.name}
                      checked={selectedStates.includes(state.id)}
                      onChange={(_e, { checked }: { checked: boolean }) =>
                        toggleState(state.id, checked)
                      }
                    />
                  </td>
                  <td className={style.treeNum}>
                    {format0(stateCounties.length)}
                  </td>
                  <td className={style.treeNum}>
                    {format0(state.count, EMPTY_CELL)}
                  </td>
                </tr>
                {expanded &&
                  shown.map((county) => (
                    <tr key={county.id}>
                      <td className={style.treeExpandCol} />
                      <td className={style.treeCountyCell}>
                        <Checkbox
                          id={`${uid}-county-${county.id}`}
                          labelText={countyLabel(county.name)}
                          checked={selectedCounties.includes(county.id)}
                          onChange={(_e, { checked }: { checked: boolean }) =>
                            toggleCounty(county.id, checked)
                          }
                        />
                      </td>
                      <td className={style.treeNum} />
                      <td className={style.treeNum}>
                        {format0(county.count, EMPTY_CELL)}
                      </td>
                    </tr>
                  ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
